<template>
  <div class="widget w-watchlist-widget" :class="['font-size-' + fontSize, { bold: isFontBold }]">
    <c-widget-header
      :csvExport="Boolean(symbols.length && !isError)"
      fieldsConfigManager="advanced"
      importWatchlist
      :hideMenu="isSidebar"
      optimizeColumns
      printView
      watchlistManager
      :wid="wid"
      performanceBar
      @csv-export="onCsvExport"
      @optimize-columns="autoSizeColumns"
    >
      <slot name="buttons" slot="buttons" />
    </c-widget-header>

    <div class="content-wrapper">
      <c-widget-control-bar v-if="controlWatchlist">
        <v-form-group v-if="isSidebar">
          <v-form-select
            valueField="id"
            textField="name"
            :options="watchlists"
            v-model="controlWatchlist"
            :aria-label="i18n('widget-title')"
          />
        </v-form-group>
        <template v-else>
          <c-widget-control-bar-group inline>
            <v-form-group :label="$t('white-label.globalLabels.symbol')" label-cols="auto">
              <c-incremental-search
                :disabled="controlWatchlist === '' || this.watchlists.length === 0"
                ref="incrementalSearch"
                size="md"
                focusOnInput
                :ric="inputSymbols"
                @error="onSymbolSearchError"
                @change-ric="addSymbol"
              />
            </v-form-group>
            <v-form-group :label="i18n('labels.list')" label-cols="auto">
              <v-form-select
                valueField="id"
                textField="name"
                :options="watchlists"
                v-model="controlWatchlist"
                :aria-label="i18n('labels.list')"
              />
            </v-form-group>
            <v-form-group :label="i18n('template', 'globalLabels')" label-cols="auto">
              <v-form-select
                :options="fieldsConfigNameOptions"
                v-model="controlFieldsConfigName"
                :aria-label="i18n('template', 'globalLabels')"
              />
            </v-form-group>
            <v-form-group :label="i18n('autosort', 'globalLabels')" label-cols="auto">
              <c-widget-input-switch :options="autosortOptions" v-model="controlAutosort" />
            </v-form-group>
          </c-widget-control-bar-group>
          <c-widget-control-bar-group inline>
            <c-manipulate-watchlist :successCallback="watchlistId => (controlWatchlist = watchlistId)" />
          </c-widget-control-bar-group>
        </template>
      </c-widget-control-bar>

      <div class="widget-body" @keydown="onKeyDown">
        <c-advances-declines
          v-if="
            showPerformanceBar &&
              !isError &&
              this.selectedWatchlist &&
              this.selectedWatchlist.items.length > 0 &&
              !isSidebar
          "
          :label="$t('white-label.widgets.portfolio.labels.advancesDeclines')"
          :advances="performanceBar.advances"
          :declines="performanceBar.declines"
          :totalAdvances="performanceBar.totalAdvances"
          :totalDeclines="performanceBar.totalDeclines"
          :totalNeutral="performanceBar.totalNeutral"
        />
        <c-loader v-if="isLoading && !isError" />
        <c-streaming-error :show="hasStreamingError" />
        <ag-grid-vue
          v-if="symbols.length && !isError"
          v-show="!isLoading"
          class="c-data-grid ag-theme-balham-dark"
          animateRows
          :asyncTransactionWaitMillis="gridUpdateInterval"
          :columnDefs="columnDefs"
          :context="this"
          :defaultColDef="defaultColumnDef"
          :fullWidthCellRenderer="fullWidthCellRenderer"
          ensureDomOrder
          :gridOptions="gridOptions"
          :groupDefaultExpanded="-1"
          treeDataDisplayType="custom"
          :rowHeight="agGridRowHeight"
          :rowClassRules="rowClassRules"
          rowSelection="multiple"
          :suppressColumnVirtualisation="isAccessibilityEnabled"
          suppressContextMenu
          suppressDragLeaveHidesColumns
          suppressFieldDotNotation
          :tooltipShowDelay="1"
          treeData
          maintainColumnOrder="false"
          :localeText="localeText"
          @column-moved="onColMoved"
          @column-resized="onColResized"
          @drag-stopped="onGridDragEnd"
          @grid-ready="onGridReady"
          @row-drag-end="onRowDragEnd"
          @row-drag-enter="onRowDragEnter"
          @row-drag-leave="onRowDragEnd"
          @row-drag-move="onRowDragMove"
          @row-group-opened="onGroupToggle"
          @row-clicked="onRowClicked"
          @row-selected="onRowSelected"
          @sort-changed="onTableSort"
          @cell-focused="onCellFocusedWidget"
          @new-columns-loaded="ev => setColumnState(ev.columnApi)"
          @async-transactions-flushed="onAsyncTransactionsFlushed"
        />
        <template v-else-if="controlWatchlist && !symbols.length">
          <v-button v-if="isSidebar" class="add-symbol" @click="openSymbolSearchDialog($event.target)"
            ><font-awesome-icon :icon="['fas', 'plus-circle']" /> {{ i18n('buttonAddSymbol') }}</v-button
          >
          <v-alert v-else align="center" show variant="light">{{ i18n('messages.noSymbols') }}</v-alert>
        </template>
        <v-alert v-else-if="isError === 'nodata'" align="center" show variant="light">{{
          i18n('messages.nodata')
        }}</v-alert>
        <v-alert v-else-if="isError" align="center" show variant="red">{{ i18n('messages.error') }}</v-alert>
      </div>

      <v-dialog ref="symbolSearchDialog">
        <template #title="{ editRow }">{{
          editRow ? `${i18n('edit', 'globalLabels')} ${editRow.data['q._TCR']}` : i18n('addSymbol')
        }}</template>
        <template #default="{ editRow }">
          <c-incremental-search
            class="symbol-search"
            :disabled="!controlWatchlist || !watchlists.length"
            size="md"
            :ric="inputSymbols"
            @error="onSymbolSearchError"
            @change-ric="(ric, ticker) => addSymbol(ric, ticker, editRow)"
          />
        </template>
      </v-dialog>

      <v-dialog
        ref="initWidgetDialog"
        class="init-widget-dialog"
        :options="{ closeOnEscape: false, focusInput: true }"
        :show="!controlWatchlist"
      >
        {{ i18n('initWidgetDialog.intro') }}
        <section>
          <div v-if="watchlists.length" class="watchlist-selection">
            <h6>{{ i18n('initWidgetDialog.openExistingList') }}</h6>
            <div class="avail-watchlists">
              <v-button
                v-for="watchlist in watchlists"
                :key="watchlist.id"
                variant="primary"
                @click="
                  controlWatchlist = watchlist.id;
                  $refs.initWidgetDialog.close();
                  setFocus();
                "
                ><span class="sr-only"> {{ i18n('initWidgetDialog.openExistingList') }}</span>
                {{ watchlist.name }}</v-button
              >
            </div>
          </div>
          <div class="watchlist-creation">
            <h6>{{ i18n('initWidgetDialog.createNewList') }}</h6>
            <v-form-group
              :state="isValidWatchlistName"
              :invalid-feedback="i18n('messages.name-duplicate', 'components.manipulateWatchlist')"
            >
              <v-form-text
                :aria-label="i18n('initWidgetDialog.createNewList')"
                autocomplete="off"
                autofocus
                :clear-label="i18n('clear', 'components.widgetInputText')"
                :maxlength="$appConfig.generalInputMaxLength"
                :state="isValidWatchlistName"
                trim
                v-model="inputNewWatchlistName"
                @keydown.enter="onCreateWatchlist"
              />
            </v-form-group>
            <v-button
              :disabled="!inputNewWatchlistName || !isValidWatchlistName"
              variant="primary"
              @click="onCreateWatchlist"
              >{{ i18n('create', 'globalLabels') }}</v-button
            >
          </div>
        </section>
      </v-dialog>
    </div>

    <b-toaster :name="notifierId"></b-toaster>
  </div>
</template>

<script>
import agGridCommons from '@mixins/agGridCommons';
import CManipulateWatchlist from '@custom/CManipulateWatchlist';
import CLoader from '@custom/CLoader';
import CStreamingError from '@custom/CStreamingError';
import CWidgetControlBar from '@custom/widgetContainer/CWidgetControlBar';
import CWidgetControlBarGroup from '@custom/widgetContainer/CWidgetControlBarGroup';
import CWidgetHeader from '@custom/widgetContainer/CWidgetHeader';
import CIncrementalSearch from '@custom/CIncrementalSearch';
import fullWidthRowRenderer from './fullWidthRowRenderer';
import tickerHeaderRenderer from './tickerHeaderRenderer';
import WatchlistRowButtonRenderer from './watchlistRowButtonRenderer';
import VDialog from '@base/VDialog';
import { API } from '@/state/api';
import { AgGridVue } from 'ag-grid-vue';
import { library as fontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { GenericTickerRenderer } from '@custom/dataGrid/renderers';
import { getConsolidatedRic } from '@/utils/helpers';
import CAdvancesDeclines from '@custom/CAdvancesDeclines';
import moment from 'moment';

import { faPlus } from '@fortawesome/pro-light-svg-icons';
fontAwesomeLibrary.add(faPlus);

export default {
  name: 'w-watchlist-widget',
  mixins: [agGridCommons],
  components: {
    AgGridVue,
    CLoader,
    CStreamingError,
    CManipulateWatchlist,
    CWidgetControlBar,
    CWidgetControlBarGroup,
    CWidgetHeader,
    CIncrementalSearch,
    fullWidthRowRenderer,
    tickerHeaderRenderer,
    WatchlistRowButtonRenderer,
    VDialog,
    GenericTickerRenderer,
    CAdvancesDeclines
  },
  props: {
    isSidebar: Boolean,
    wid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      chainsLoaded: new Set(),
      footerRowKey: '_ROW.FOOTER',
      emptyRowKey: '_ROW.EMPTY',
      localeText: null,
      fullWidthCellRenderer: 'fullWidthRowRenderer',
      gridOptions: {
        isFullWidthCell: node => node.data && (node.data.isFooterRow || node.data.isEmptyRow),
        fullWidthCellRendererParams: () => {
          return { wid: this.wid };
        },
        isRowSelectable: node => !node.data || !node.data.isFooterRow,
        getDataPath: data => {
          const path = [];
          if (data.group) path.push(data.group);
          if (data.chain) path.push(data.chain);
          if (data.isEmptyRow) path.push(`ric:${data.id}`);
          // in case RIC === group, DataPath would be x/x, which makes ag-grid merge the ric row with the group row. Avoid this by adding a random prefix to the ric part
          else path.push('ric:' + data['q.RIC']);
          return path;
        },
        getRowNodeId: data => {
          if (data.isEmptyRow) {
            return data.id;
          }
          return data.isFooterRow ? this.footerRowKey : (data.chain || this.noChainNS) + '/' + data['q.RIC'];
        },
        postSort: rowNodes => {
          const lastRow = rowNodes.slice(-1)[0];
          if (lastRow && lastRow.id !== this.footerRowKey) {
            const footerRowIndex = rowNodes.findIndex(n => n.id === this.footerRowKey);
            if (footerRowIndex >= 0) rowNodes.push(...rowNodes.splice(footerRowIndex, 1));
          }
        },
        columnTypes: { actionable: {} },
        tabToNextHeader: this.tabToNextHeader,
        tabToNextCell: this.tabToNextCell
      },
      hasStreamingError: false,
      inputNewWatchlistName: '',
      inputSymbols: '',
      isError: false,
      isLoading: false,
      noChainNS: 0,
      resolvedChains: {},
      rowClassRules: {
        'ag-row-dragging': 'node.dragging'
      },
      rowSelectionTimeout: 0,
      tableFieldsPrepend: ['action']
    };
  },
  computed: {
    customTickerSort: {
      get() {
        return !this.controlAutosort || this.widgetStore.customTickerSort;
      },
      set(value) {
        if (this.controlAutosort) this.$store.commit(`widget/${this.wid}/CUSTOM_TICKER_SORT`, value);
      }
    },
    controlWatchlist: {
      get() {
        return this.widgetStore.watchlist;
      },
      set(payload) {
        this.$store.commit(`widget/${this.wid}/WATCHLIST_CHANGED`, payload);
        this.fetch();
        this.netChanges = {};
      }
    },
    extendedColDefs() {
      return {
        action: {
          cellRenderer: 'WatchlistRowButtonRenderer',
          maxWidth: this.isSidebar ? 20 : 30,
          resizable: this.isSidebar ? false : true
        },
        'q._TCR': {
          cellRenderer: 'agGroupCellRenderer',
          cellRendererParams: {
            suppressCount: true,
            innerRendererFramework: 'GenericTickerRenderer',
            innerRendererParams: params => {
              if (params.data) {
                return {
                  ticker: params.data['q._TCR'],
                  tradeHaltValue: params.data['q.TRD_STATUS'],
                  priceChange: params.data['q._NETCHNG_1']
                };
              } else {
                return { ticker: params.value };
              }
            }
          },
          headerComponent: 'tickerHeaderRenderer',
          comparator: (val1, val2, node1, node2, isInverted) => {
            // groups are always compared using custom sort/initial order.
            if (this.customTickerSort || (node1.group && node2.group)) {
              return this.customComparator(node1, node2, isInverted);
            } else {
              if (val1.value && val2.value) return val1.value.localeCompare(val2.value);
              else if (val1.value) return -1;
              else if (val2.value) return 1;
              return 0;
            }
          },
          width: this.isSidebar ? 65 : 115,
          rowDrag: params => params.node.group || !params.node.data.chain,
          showRowGroup: true,
          valueGetter: params => {
            if (params.data) {
              let priceChange = params.data['q._NETCHNG_1'];
              let change = '';
              if (priceChange > 0) change = 'positive';
              else if (priceChange < 0) change = 'negative';
              return { value: params.data['q._TCR'], change, toString: () => params.data['q._TCR'] };
            } else {
              return params.node.key;
            }
          },
          equals: (val1, val2) => {
            let tickerEqual = val1.value === val2.value;
            let changeEqual = val1.change === val2.change;
            return tickerEqual && changeEqual;
          },
          type: ['actionable'],
          pinned: this.isSidebar ? false : this.relatedFieldsConfig.pinnedFids.includes('q._TCR')
        }
      };
    },
    groups() {
      return (this.selectedWatchlist && this.selectedWatchlist.groups) || {};
    },
    groupsCollapsed() {
      return this.widgetStore.groupsCollapsed[this.controlWatchlist] || [];
    },
    isValidWatchlistName() {
      return this.inputNewWatchlistName
        ? this.watchlists.findIndex(
            watchlist => watchlist.name.toLowerCase() === this.inputNewWatchlistName.trim().toLowerCase()
          ) < 0
        : undefined;
    },
    linkModeSymbols() {
      return this.widgetStore.addSymbols;
    },
    selectedWatchlist() {
      return this.controlWatchlist ? this.$store.getters['watchlists/getWatchlist'](this.controlWatchlist) : null;
    },
    symbols() {
      return (this.selectedWatchlist && this.selectedWatchlist.items) || [];
    },
    watchlists() {
      return this.$store.getters['watchlists/watchlistsSorted'];
    },
    emptyRowsData() {
      const items = (this.selectedWatchlist && this.selectedWatchlist.items) || [];
      return items.reduce((acc, e, index) => {
        if (e.includes(this.emptyRowKey)) {
          acc.push({
            id: e,
            position: index,
            group: this.groups[e] || null,
            isEmptyRow: true
          });
        }
        return acc;
      }, []);
    },
    isRowDraggable() {
      // Only cases where row drag is enabled matter.
      return this.customTickerSort;
    }
  },
  watch: {
    controlWatchlist: {
      handler(watchlistId) {
        if (!watchlistId) {
          if (this.isSidebar && this.watchlists.length) this.controlWatchlist = this.watchlists[0].id;
          else if (this.$refs.initWidgetDialog) this.$refs.initWidgetDialog.open();
        }
      },
      immediate: true
    },
    linkModeSymbols: {
      handler(symbols) {
        if (symbols.length && this.controlWatchlist) {
          symbols.forEach(symbol => {
            if (!this.symbols.includes(symbol)) {
              this.$store.dispatch('watchlists/addRICs', { id: this.controlWatchlist, rics: [symbol] });
              this.streamingUnsubscribe();
              this.resolveChains(symbol)
                .then(() => this.getSnapshot(symbol))
                .then(() => {
                  this.highlightRow(symbol);
                  this.streamingSubscribe(this.getRequestRics());
                });
            } else {
              this.highlightRow(symbol);
            }
          });
          this.$store.commit(`widget/${this.wid}/LINK_UPDATED`, '');
        }
      },
      immediate: true
    },
    watchlists() {
      if (!this.watchlists.find(w => w.id === this.controlWatchlist))
        this.controlWatchlist = (this.isSidebar && this.watchlists.length && this.watchlists[0].id) || '';
    }
  },
  methods: {
    customComparator(node1, node2, isInverted) {
      let position1 = (node1.group ? node1.allLeafChildren[0] : node1).data.position;
      let position2 = (node2.group ? node2.allLeafChildren[0] : node2).data.position;

      if (isNaN(position1)) position1 = Infinity;
      if (isNaN(position2)) position2 = Infinity;
      if (node1.group && node2.group && isInverted) return position2 - position1;
      return position1 - position2;
    },
    sortComparator(value1, value2, node1, node2, isInverted) {
      // groups are always compared using custom sort/initial order.
      if (node1.group && node2.group) return this.customComparator(node1, node2, isInverted);

      return agGridCommons.methods.sortComparator.call(this, value1, value2, node1, node2, isInverted);
    },
    setFocus() {
      this.$nextTick(() => {
        const el = this.$parent.$el.querySelector('.c-widget-control-bar-group input[type="text"]');
        if (el) el.focus();
      });
    },
    addSymbol(_ric, ticker, rowNode) {
      let ric = _ric;

      if (!ric) {
        console.error('Incremental search returned empty symbol');
        return;
      }

      if (ric === '*') {
        this.onSymbolSearchError({ type: 'INVALID_RIC', ric: '*' });
        return;
      }

      ric = ric.replace('=X', '=');
      if (this.symbols.includes(ric)) {
        this.highlightRow(ric, true);
      } else {
        let replaceRIC;
        const subId = this.subscriptionIds[0];
        if (rowNode) {
          replaceRIC = rowNode.data['q.RIC'];
          this.$store.dispatch('watchlists/replaceRIC', {
            id: this.controlWatchlist,
            ric: replaceRIC,
            newRic: ric
          });
          if (subId) {
            API.Streaming.Quote.removeRics(subId, [replaceRIC]);
          }
        } else {
          if (!this.symbols.length) this.isLoading = true;
          this.$store.dispatch('watchlists/addRICs', { id: this.controlWatchlist, rics: [ric] });
        }
        this.resolveChains(ric)
          .then(() => {
            return this.getSnapshot(ric, replaceRIC);
          })
          .then(() => {
            this.highlightRow(ric);
            if (subId) {
              API.Streaming.Quote.addRics(subId, [ric]);
              this.ulStreamingSubscription([ric], true);
            } else {
              this.streamingSubscribe(this.getRequestRics());
            }
          });
        this.inputSymbols = '';
      }
      this.$refs.symbolSearchDialog.close();
    },
    addEmptyRow(rowNode, group) {
      let position;
      if (rowNode.group) {
        position = this.getSymbolPositionByGroup(rowNode.groupData['q._TCR']);
      } else {
        const symbol = rowNode.data['q.RIC'];
        position = this.symbols.findIndex(e => e === symbol);
        position = position === -1 ? 0 : position;
      }

      this.addEmptyRowByPosition(position, group);
    },
    addEmptyRowByPosition(position, group) {
      const uniqueRowId = `${this.emptyRowKey}_${new Date().getTime()}`;
      this.$store.dispatch('watchlists/addEmptyRow', {
        id: this.controlWatchlist,
        emptyRowId: uniqueRowId,
        position,
        group
      });
      //insert the row in the grid and update the rest of the rows
      this.getSnapshot().then(() => {
        const rowNode = this.gridOptions.api.getRowNode(uniqueRowId);
        if (rowNode) {
          this.gridOptions.api.ensureIndexVisible(rowNode.rowIndex, 'middle');
          this.gridOptions.api.setFocusedCell(rowNode.rowIndex, 'q._TCR');
        }
      });
    },
    highlightRow(ric, focusTickerCell) {
      let searchNode;
      this.gridOptions.api.forEachNode(node => {
        if (node.data && node.data['q.RIC'] === ric) {
          searchNode = node;
          return;
        }
      });
      if (searchNode) {
        searchNode.setSelected(true, true);
        if (searchNode.parent && searchNode.parent.group) {
          searchNode.parent.setExpanded(true);
        }

        let rowIndex = searchNode.rowIndex;
        if (rowIndex == null) {
          rowIndex = (searchNode.childIndex || 0) + (searchNode.parent ? searchNode.parent.rowIndex : 0) + 1;
        }
        setTimeout(() => {
          this.gridOptions.api.ensureIndexVisible(rowIndex, 'top');
          if (focusTickerCell) {
            this.gridOptions.api.setFocusedCell(rowIndex, 'q._TCR');
          }
        }, 500);
      }
    },
    onCreateWatchlist() {
      // prevent submit when pressing enter while input is invalid
      if (!this.inputNewWatchlistName || !this.isValidWatchlistName) {
        return;
      }
      this.$store.dispatch(`watchlists/createWatchlist`, { name: this.inputNewWatchlistName }).then(watchlistId => {
        this.inputNewWatchlistName = '';
        this.controlWatchlist = watchlistId;
        this.$refs.initWidgetDialog.close();
        this.setFocus();
      });
    },
    // TODO consider merging with the common onCsvExprt in agGridCommons
    onCsvExport() {
      this.gridOptions.api.exportDataAsCsv({
        columnKeys: this.gridOptions.columnApi.getAllGridColumns().reduce((acc, col) => {
          if (!acc.length) acc[0] = 'group';
          if (col.colDef.field !== 'action' && !this.excludedCsvExportFids.includes(col.colDef.field))
            acc.push(col.colId);
          return acc;
        }, []),
        fileName: `${this.i18n('widget-title')} ${this.selectedWatchlist.name.replace(
          RegExp(`^${this.i18n('widget-title')}[\\W_]*`, 'i'),
          ''
        )}.csv`,
        processCellCallback(params) {
          if (params.value === undefined || params.value === null || params.value.toString() === 'NaN') return '';
          if (params.value instanceof Date) return params.value.toLocaleDateString('en-US');

          let momentDate = (momentDate = moment(params.value, 'DD MMM YYYY', true));
          if (momentDate.isValid()) {
            return momentDate.format('MM/DD/YYYY');
          }

          return params.value.toString();
        },
        processHeaderCallback(params) {
          return params.column.colDef.headerName;
        },
        shouldRowBeSkipped: params => params.node.id && params.node.id === this.footerRowKey,
        skipGroups: true
      });
    },
    onGroupToggle(ev) {
      this.$store.commit(`widget/${this.wid}/GROUP_COLLAPSED`, {
        watchlistId: this.controlWatchlist,
        group: ev.node.key,
        collapsed: !ev.node.expanded
      });
    },
    onKeyDown(ev) {
      const key = ev.code || ev.key;

      if (['Delete', 'Backspace'].includes(key)) {
        const isInputInFocus = ['input', 'select'].indexOf(document.activeElement.tagName.toLowerCase()) !== -1;
        if (!isInputInFocus) {
          this.removeSymbols(this.gridOptions.api.getSelectedNodes());
        }
      }
    },
    onSymbolSearchError(ev) {
      if (ev.type === 'INVALID_RIC') {
        this.$notify.error('', this.i18n('invalidSymbol', 'globalMessages', { symbol: ev.ric }), {
          group: this.notifierId,
          closeButton: true,
          duration: -1
        });
      } else if (ev.type === 'EMPTY_RIC') {
        this.addEmptyRowByPosition(this.symbols.length);
        this.$refs.symbolSearchDialog.close();
      } else console.error(ev);
    },

    // #region row dragging
    onRowDragEnter(ev) {
      if (ev.node.group && ev.node.expanded) {
        ev.node.allLeafChildren.forEach(node => (node.dragging = true));
        this.gridOptions.api.applyTransaction({ update: ev.node.allLeafChildren.map(node => node.data) });
      }
    },
    onRowDragMove(ev) {
      if (
        !ev.overNode ||
        !ev.vDirection ||
        ev.node === ev.overNode ||
        (ev.node.group && !ev.overNode.group && ev.overNode.data.group) ||
        !this.isRowDraggable
      )
        return;

      if (ev.overNode.id === this.footerRowKey) {
        if (!ev.node.group && ev.node.data.group) {
          ev.node.data.group = undefined;
          if (this.gridOptions && this.gridOptions.api)
            this.gridOptions.api.applyTransactionAsync({ update: [ev.node.data] });
        }
        return;
      }
      const dragNode = ev.node.group ? ev.node.childrenAfterSort[0] : ev.node;
      const overNode = ev.overNode.group ? ev.overNode.childrenAfterSort[0] : ev.overNode;
      let overNodeTargetPos = dragNode.data.position;
      let dragNodeTargetPos = overNode.data.position;
      if (
        (ev.vDirection === 'up' && overNodeTargetPos < dragNodeTargetPos) ||
        (ev.vDirection === 'down' && overNodeTargetPos > dragNodeTargetPos)
      ) {
        return;
      }
      // as the node position swap is implemented by exploiting sorting, we set the "position" property in node.data and let the ag-grid sorting do the swap
      // we also need to update all child-nodes positions for dragged/affected groups
      if (ev.node.group) {
        if (ev.vDirection === 'down') {
          dragNodeTargetPos -= ev.node.childrenAfterSort.length - 1;
          if (ev.overNode.group) {
            dragNodeTargetPos += ev.overNode.childrenAfterSort.length - 1;
          }
        } else {
          overNodeTargetPos += ev.node.childrenAfterSort.length - 1;
          if (ev.overNode.group) {
            overNodeTargetPos -= ev.overNode.childrenAfterSort.length - 1;
          }
        }
        ev.node.childrenAfterSort.forEach((cnode, i) => {
          cnode.data.position = dragNodeTargetPos + (cnode.data.chain ? 0 : i);
        });
        if (ev.overNode.group) {
          ev.overNode.childrenAfterSort.forEach((cnode, i) => {
            cnode.data.position = overNodeTargetPos + (cnode.data.chain ? 0 : i);
          });
        }
      } else {
        if (ev.overNode.group && !ev.overNode.childrenAfterSort[0].data.chain) {
          if (ev.overNode.expanded) {
            ev.node.data.group = ev.vDirection === 'down' ? overNode.data.group : undefined;
          } else {
            if (ev.vDirection === 'down') {
              dragNodeTargetPos += ev.overNode.childrenAfterSort.length - 1;
            } else {
              overNodeTargetPos -= ev.overNode.childrenAfterSort.length - 1;
            }
            dragNode.data.position = dragNodeTargetPos;
            ev.overNode.childrenAfterSort.forEach((cnode, i) => {
              cnode.data.position = overNodeTargetPos + (cnode.data.chain ? 0 : i);
            });
          }
        } else if (dragNode.data.group === overNode.data.group) {
          dragNode.data.position = dragNodeTargetPos;
        }
      }
      if (!ev.overNode.group) {
        if (ev.node.group || dragNode.data.group === overNode.data.group) {
          overNode.data.position = overNodeTargetPos;
        } else {
          dragNode.data.group = overNode.data.group;
        }
      }
      // offload the row parsing to unbusy time
      if (this.gridOptions && this.gridOptions.api)
        this.gridOptions.api.applyTransactionAsync({ update: [dragNode.data, overNode.data] });
    },
    onRowDragEnd(ev) {
      let symbol;
      if (!this.isRowDraggable) {
        return;
      } else if (ev.node.group) {
        symbol = ev.node.allLeafChildren[0].data.chain
          ? '0#' + ev.node.key
          : ev.node.childrenAfterSort[0].data['q.RIC'];
        if (ev.node.expanded) {
          ev.node.allLeafChildren.forEach(node => (node.dragging = false));
          this.gridOptions.api.applyTransaction({ update: ev.node.allLeafChildren.map(node => node.data) });
        }
      } else if (ev.node.data.isEmptyRow) {
        symbol = ev.node.data.id;
      } else {
        symbol = ev.node.data['q.RIC'];
      }
      let rowList = [];
      this.gridOptions.api.forEachNodeAfterFilterAndSort(rowNode => {
        if (rowNode.data && !rowNode.data.isFooterRow) {
          rowList.push(rowNode.data.isEmptyRow ? rowNode.data.id : rowNode.data['q.RIC']);
        }
      });
      this.$store.dispatch('watchlists/saveWatchlistPositions', {
        id: this.controlWatchlist,
        list: rowList,
        ric: symbol,
        group: (ev.node.group && ev.node.childrenAfterSort[0].data.group) || ev.node.data.group
      });
    },
    // #endregion

    onRowClicked(ev) {
      // as ag-grid doesn't automatically select group rows on click, let's select by ourselves. Clear selection of other rows if neither of ctrl/shift key were used
      if (ev.node.group) ev.node.setSelected(true, !ev.event.ctrlKey && !ev.event.shiftKey);
    },
    onRowSelected(ev) {
      if (!ev.node.selected || ev.node.group) return;
      // avoid firing unnecessary events on multiple rows select, as we are only interested in the last one
      clearTimeout(this.rowSelectionTimeout);
      this.rowSelectionTimeout = setTimeout(() => this.updateLink(ev), 75);
    },
    onTableSort({ api, columnApi }) {
      const sortState = this.getSortState();

      if (!sortState.length) {
        this.customTickerSort = true;
        columnApi.applyColumnState({ state: [{ colId: 'q._TCR', sort: 'asc' }] });
      } else {
        let suppressRowDrag = false;

        if (this.controlAutosort) {
          this.$store.commit(`widget/${this.wid}/TABLE_SORT_CHANGED`, sortState);
          if (!this.customTickerSort || sortState[0].colId !== 'q._TCR') {
            suppressRowDrag = true;
            this.customTickerSort = false;
          }
        }
        api.setSuppressRowDrag(suppressRowDrag);
      }
    },
    setCustomTickerSort(isCustom) {
      this.customTickerSort = isCustom;
    },
    openSymbolSearchDialog(fromEl, editRow) {
      this.$refs.symbolSearchDialog.open({
        closeOnEscape: true,
        focusInput: true,
        params: {
          editRow
        },
        position: {
          relativeTo: fromEl,
          attach: 'on-top'
        }
      });
    },
    removeSymbols(rowNodes) {
      if (!Array.isArray(rowNodes)) rowNodes = [rowNodes];
      const remove = rowNodes.reduce(
        (acc, node) => {
          if (node.group) {
            node.allLeafChildren.forEach((subnode, i) => {
              acc.rows.push(subnode.data);
              if (subnode.data.chain) {
                // add chain ric to removal list only once
                if (i === 0) {
                  acc.rics.push('0#' + node.key);
                  this.chainsLoaded.delete(node.key);
                }
              } else acc.rics.push(subnode.data['q.RIC']);
            });
          } else {
            acc.rows.push(node.data);
            acc.rics.push(node.data.isEmptyRow ? node.data.id : node.data['q.RIC']);
          }
          return acc;
        },
        { rows: [], rics: [] }
      );
      const subId = this.subscriptionIds[0];
      if (subId) {
        API.Streaming.Quote.removeRics(subId, remove.rics);
      }
      this.gridOptions.api.applyTransaction({
        remove: remove.rows
      });
      this.$store.dispatch('watchlists/deleteRICs', { id: this.controlWatchlist, rics: remove.rics });
      remove.rics.forEach(ric => {
        this.$delete(this.netChanges, ric);
      });
      this.ulStreamingRemoveRic(remove.rics, this.getRequestRics());
    },
    resolveChains(symbol) {
      const symbols = symbol ? [symbol] : this.symbols;
      const pendingRequests = [];
      symbols.forEach(symbol => {
        if (!symbol.startsWith('0#')) return;
        const chain = symbol.substr(2);
        if (this.resolvedChains[chain]) return;

        pendingRequests.push(
          API.Rest.get(`/index/constituents?ric=${getConsolidatedRic(chain)}&fids=q.RIC`).then(response => {
            if (!response.data.data.length) {
              console.error('No data for chain symbol', symbol);
            } else {
              this.resolvedChains[chain] = response.data.data.map(ricObj => ricObj['q.RIC']);
            }
          })
        );
      });
      return Promise.all(pendingRequests);
    },
    getRequestRics(inputRics = this.symbols) {
      const outputRics = new Set();
      inputRics.forEach(ric => {
        if (ric.startsWith('0#')) {
          const chain = ric.substr(2);
          if (this.resolvedChains[chain]) {
            this.resolvedChains[chain].forEach(ric => outputRics.add(ric));
          }
        } else if (!ric.includes(this.emptyRowKey)) {
          outputRics.add(ric);
        }
      });
      return [...outputRics];
    },
    getSnapshot(addSymbol, replaceSymbol) {
      const symbols = addSymbol ? [addSymbol] : this.symbols;
      const addIndex = addSymbol ? this.symbols.indexOf(addSymbol) : 0;

      //To handle whitespaces in the RICs
      const rics = this.getRequestRics(symbols)
        .map(ric => ric.replace(/\s+/g, '_')) //replace with 'underscore' as the RIC is anyways invalid
        .join(',');
      return API.Rest.get(
        `/quote/info?rics=${encodeURIComponent(rics)}&fids=${encodeURIComponent(this.requestFids)}`
      ).then(response => {
        Object.entries(response.data.metadata).forEach(([fid, { type }]) => {
          if (!this.formats[fid]) {
            this.formats[fid] = type;
          }
        });
        this.isError = false;
        const dataSnapshot = this._.keyBy(response.data.data, 'q.RIC');
        const rowsData = [];
        const symbolsIterator = (symbols, chain, chainPos) => {
          symbols.forEach((symbol, pos) => {
            if (addIndex) pos = addIndex;
            if (symbol.startsWith('0#')) {
              const chain = symbol.substr(2);
              if (this.resolvedChains[chain]) {
                symbolsIterator(this.resolvedChains[chain], chain, pos);
                return;
              }
            } else if (symbol.includes(this.emptyRowKey)) {
              return;
            }

            const data = dataSnapshot[symbol] || {
              ...this.requestFids.reduce((acc, fid) => {
                acc[fid] = '-';
                return acc;
              }, {}),
              'q.RIC': symbol,
              'q._TCR': symbol.replace('0#', 'ᐅ '),
              'q._DSPLY_NAME': this.i18n('symbolNA', 'globalLabels'),
              NA: true
            };
            if (this.gridOptions && this.gridOptions.api) {
              const row = this.gridOptions.api.getRowNode(chain + '/' + data['q.RIC']);
              const rowData = this.hydrateData(
                data,
                // clone row data object, otherwise ag-grid won't detect if there were any changes (must be new object reference)
                row ? row.data : {}
              );
              rowData.chain = chain || null;
              if (!chain) rowData.group = this.groups[symbol];
              // needs to be defined for rows drag'n'drop, we let the ag-grid sort rows by this property
              rowData.position = chain ? chainPos : pos;
              rowsData.push(rowData);
            }
          });
          this.chainsLoaded.add(chain);
        };
        symbolsIterator(symbols, this.noChainNS);
        rowsData.push(...this.emptyRowsData);
        if (addSymbol) {
          const firstSymbolAdd = this.symbols.length === 1 && !replaceSymbol;
          if (firstSymbolAdd) {
            rowsData.push({ isFooterRow: true });
          }
          this.gridOptions.api.applyTransaction({
            add: rowsData,
            addIndex,
            remove: replaceSymbol ? [this.gridOptions.api.getRowNode('0/' + replaceSymbol).data] : undefined
          });
        } else {
          rowsData.push({ isFooterRow: true });
          this.gridOptions.api.setRowData(rowsData);
          // set collapsed state on group rows
          if (this.groupsCollapsed.length) {
            this.gridOptions.api.forEachNode(node => {
              if (this.groupsCollapsed.includes(node.key)) node.expanded = false;
            });
            this.gridOptions.api.onGroupExpandedOrCollapsed();
          }
        }
        this.isLoading = false;
      });
    },
    handleSnapshot(response, error) {
      if (error) {
        this.hasStreamingError = true;
        return;
      }
      this.handleUpdate(response);
    },
    handleUpdate(response, error) {
      if (error) {
        console.error('Streaming update failed:', error);
        this.hasStreamingError = true;
        return;
      }
      if (!this.gridOptions || !this.gridOptions.api) return;
      this.hasStreamingError = false;
      response = response.payload[0];
      this.chainsLoaded.forEach(chain => {
        const rowNode = this.gridOptions.api.getRowNode(chain + '/' + response.ric);
        // if there's no row for this chain + ric, then just do nuthin
        if (!rowNode) return;
        const data = this.hydrateData(response.data, rowNode.data);
        if (this.gridOptions && this.gridOptions.api) {
          this.gridOptions.api.applyTransactionAsync({ update: [data] });
        }
      });
    },
    fetch() {
      clearTimeout(this.connectionRetryTimeout);
      this.streamingUnsubscribe();
      if (!this.symbols.length) return;
      this.isLoading = true;
      // reset table on re-fetch to avoid cell renderers to attempt rendering before the data arrives
      if (this.gridOptions.api) this.gridOptions.api.setRowData([]);

      this.resolveChains()
        .then(() => this.getSnapshot())
        .catch(e => {
          this.isError = true;
          console.error(e);
          this.connectionRetryTimeout = setTimeout(this.fetch, 60000);
        })
        .then(() => this.streamingSubscribe(this.getRequestRics()));
    },
    suppressKeyboardEvent(params) {
      function getEventPath(event) {
        const path = [];
        let currentTarget = event.target;
        while (currentTarget) {
          path.push(currentTarget);
          currentTarget = currentTarget.parentElement;
        }
        return path;
      }
      function getAllFocusableElementsOf(el) {
        return Array.from(
          el.querySelectorAll(
            'button:not(.dropdown-item), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
        ).filter(focusableEl => {
          return focusableEl.tabIndex !== -1;
        });
      }

      // If the next row is footer row, suppress built-in tabbing
      if (params.event.key === 'Tab') {
        const path = getEventPath(params.event);
        const eGridCell = path.find(el => {
          if (el.classList === undefined) return false;
          return el.classList.contains('ag-full-width-row');
        });

        //Full width empty row
        if (eGridCell) {
          //Handle jumping out of an empty row
          const focusableChildrenElements = getAllFocusableElementsOf(eGridCell);
          if (focusableChildrenElements.length > 0) {
            const lastCellChildEl = focusableChildrenElements[focusableChildrenElements.length - 1];
            const isLastChildFocused = lastCellChildEl && document.activeElement === lastCellChildEl;
            const offset = params.event.shiftKey ? -1 : 1;

            if (!isLastChildFocused && !params.event.shiftKey) {
              //don't let the focus go out of the full width row
              return true;
            } else {
              const nextRow = this.$el.querySelector(`.ag-row[row-index="${params.node.rowIndex + offset}"]`);
              if (!nextRow) return false;

              const isEmptyRow = nextRow.getAttribute('row-id').startsWith('_ROW.');
              if (!params.event.shiftKey) {
                if (!isEmptyRow) {
                  const actionEle = this.$el.querySelector(
                    `.ag-row[row-index="${params.node.rowIndex + offset}"] > div.ag-cell[col-id="action"]`
                  );
                  actionEle.focus();
                }
              } else {
                if (isEmptyRow) {
                  const button = nextRow.querySelector(`button`);
                  this.$nextTick(() => {
                    button.focus();
                  });
                }
              }
            }
          }
        } else {
          //Handle jumping out of a regular row to a full width row
          const offset = params.event.shiftKey ? -1 : 1;
          const nextRow = this.$el.querySelector(`.ag-row[row-index="${params.node.rowIndex + offset}"]`);
          const isEmptyRow = nextRow && nextRow.getAttribute('row-id').startsWith('_ROW.');
          if (isEmptyRow) {
            //Check if it's the last column
            const columns = params.columnApi.getAllDisplayedColumns();
            let columnIndex = columns.findIndex(column => column.colId === params.column.colId);
            let isMovingToNextRow = true;
            if (columnIndex !== -1) columnIndex += params.event.shiftKey ? -1 : 1;
            while (columnIndex >= 0 && columnIndex < columns.length) {
              const col = columns[columnIndex];
              if (col.colDef.type && col.colDef.type.includes('actionable')) {
                isMovingToNextRow = false;
                break;
              }
              columnIndex += params.event.shiftKey ? -1 : 1;
            }
            if (isMovingToNextRow) {
              const focusableChildrenElements = getAllFocusableElementsOf(nextRow);
              this.$nextTick(() => {
                //focus the function button
                focusableChildrenElements[0].focus();
              });
              return false;
            }
          }
        }
      }

      return false;
    },
    onCellFocusedWidget(params) {
      const column = params.column;
      if (!column) {
        this.isTabPressed = false;
        this.isShiftTabPressed = false;
        return;
      }
      if (column.colId === 'q._TCR') {
        let node = column.gridApi.getDisplayedRowAtIndex(params.rowIndex);
        if (node.group) {
          this.isTabPressed = false;
          this.isShiftTabPressed = false;
          return;
        }
      }
      return this.onCellFocused(params);
    },
    onGridReady(ev) {
      this.onTableReady(ev, this.i18n('widget-title'));
    },
    getSymbolPositionByGroup(groupName) {
      const filteredSymbols = Object.entries(this.groups).reduce((acc, [key, value]) => {
        if (value === groupName) acc.push(key);
        return acc;
      }, []);
      return filteredSymbols.reduce((minIndex, symbol) => {
        const index = this.symbols.findIndex(e => e === symbol);
        if (index != -1 && index < minIndex) {
          return index;
        }
        return minIndex;
      }, Infinity);
    }
  },
  // lifecycle
  created() {
    if (this.symbols[0]) this.fetch();
    this.localeText = {
      ariaRowSelect: this.i18n('functionButtonMenu.emptyRow')
    };
  },
  beforeDestroy() {
    this.streamingUnsubscribe();
    if (this.gridOptions) this.gridOptions = null;
    if (this.gridApi) this.gridApi = null;
  }
};
</script>

<style lang="scss">
@import '~@/styles/vendor/ag-grid';
@import '~@/styles/widgets/watchlist-widget';
</style>
